<template>
  <div class="app-shell">
    <aside class="side-nav" aria-label="主导航">
      <router-link class="brand" to="/" aria-label="翻书，最近">
        <span class="brand-mark"><nricon name="reading" :size="20" /></span>
        <span class="brand-name">翻书</span>
      </router-link>

      <nav class="nav-links">
        <router-link
          v-for="item in primaryLinks"
          :key="item.to"
          :to="item.to"
          class="nav-link"
          :class="{ 'is-current': isCurrent(item) }"
          :aria-current="isCurrent(item) ? 'page' : null"
          :aria-label="item.label"
        >
          <nricon :name="item.icon" :size="20" />
          <span>{{ item.label }}</span>
        </router-link>
      </nav>

      <div class="nav-bottom">
        <button class="nav-link theme-link" type="button" :aria-label="themeLabel" @click="cycleTheme">
          <nricon :name="themeIcon" :size="20" />
          <span>外观</span>
        </button>
        <router-link v-if="isLoggedIn" class="nav-link" to="/profile" aria-label="个人中心">
          <span class="avatar">{{ firstLetter }}</span>
          <span>个人中心</span>
        </router-link>
        <router-link v-else class="nav-link" to="/login" aria-label="登录">
          <nricon name="user" :size="20" />
          <span>登录</span>
        </router-link>
        <router-link v-if="isLoggedIn" class="add-link" to="/upload">
          <nricon name="plus" :size="18" />
          <span>添加内容</span>
        </router-link>
      </div>
    </aside>

    <main class="app-main" id="main-content" tabindex="-1">
      <header class="mobile-topbar">
        <router-link class="mobile-brand" to="/"><span class="brand-mark"><nricon name="reading" :size="18" /></span><strong>翻书</strong></router-link>
        <div class="mobile-actions">
          <button class="mobile-icon" type="button" :aria-label="themeLabel" @click="cycleTheme"><nricon :name="themeIcon" :size="19" /></button>
          <router-link v-if="isLoggedIn" class="mobile-avatar" to="/profile" aria-label="个人中心">{{ firstLetter }}</router-link>
          <router-link v-else class="mobile-icon" to="/login" aria-label="登录"><nricon name="user" :size="19" /></router-link>
        </div>
      </header>
      <div class="app-stage"><router-view /></div>
    </main>

    <nav class="bottom-nav" aria-label="主导航">
      <router-link
        v-for="item in mobileLinks"
        :key="item.to"
        :to="item.to"
        class="mobile-link"
        :class="{ 'is-current': isCurrent(item) }"
        :aria-current="isCurrent(item) ? 'page' : null"
      >
        <nricon :name="item.icon" :size="21" />
        <span>{{ item.label }}</span>
      </router-link>
    </nav>
  </div>
</template>

<script>
const primaryLinks = [
  { to: '/', label: '最近', icon: 'time', exact: true },
  { to: '/library', label: '我的书库', icon: 'collection' },
  { to: '/discover', label: '公共书库', icon: 'collection-tag' },
  { to: '/notes', label: '笔记', icon: 'notebook-1' }
]

export default {
  name: 'AppShell',
  data() {
    return { primaryLinks, mobileLinks: primaryLinks }
  },
  computed: {
    isLoggedIn() { return !!this.$store.state.token },
    firstLetter() {
      const username = this.$store.state.user && this.$store.state.user.username
      return username ? username.charAt(0).toUpperCase() : '?'
    },
    theme() { return this.$store.state.theme || 'system' },
    themeIcon() { return this.theme === 'dark' ? 'sunny' : 'setting' },
    themeLabel() {
      const label = this.theme === 'system' ? '跟随系统' : (this.theme === 'dark' ? '深色' : '浅色')
      return `切换应用外观，当前${label}`
    }
  },
  methods: {
    isCurrent(item) {
      if (item.exact) return this.$route.path === item.to
      if (item.to === '/library') return this.$route.path === '/library'
      if (item.to === '/discover') return this.$route.path === '/discover'
      return this.$route.path === item.to || this.$route.path.indexOf(item.to + '/') === 0
    },
    cycleTheme() {
      const next = { system: 'light', light: 'dark', dark: 'system' }[this.theme] || 'system'
      this.$store.commit('setTheme', next)
    }
  }
}
</script>

<style lang="scss" scoped>
.app-shell { min-height: 100vh; }
.side-nav {
  position: fixed;
  z-index: 40;
  inset: 0 auto 0 0;
  display: flex;
  width: 216px;
  flex-direction: column;
  padding: 22px 16px 18px;
  border-right: 1px solid var(--nr-border);
  background: var(--nr-surface);
}
.brand {
  display: flex;
  min-height: 48px;
  align-items: center;
  gap: 11px;
  padding: 0 10px;
  color: var(--nr-text);
  text-decoration: none;
}
.brand-mark {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  place-items: center;
  color: var(--nr-on-accent);
  border-radius: 11px;
  background: var(--nr-accent);
}
.brand-name { font-family: var(--nr-font-display); font-size: 20px; font-weight: 700; letter-spacing: .08em; }
.nav-links { display: grid; gap: 6px; margin-top: 32px; }
.nav-link {
  display: flex;
  min-height: 44px;
  align-items: center;
  gap: 13px;
  padding: 0 12px;
  color: var(--nr-text-2);
  border: 0;
  border-radius: 10px;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: color 180ms ease, background 180ms ease;
  &:hover { color: var(--nr-text); background: var(--nr-surface-2); }
  &.is-current { color: var(--nr-accent-text); background: var(--nr-accent-bg); font-weight: 650; }
}
.nav-bottom { display: grid; gap: 6px; margin-top: auto; }
.avatar {
  display: inline-grid;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  place-items: center;
  color: var(--nr-accent-text);
  border-radius: 50%;
  background: var(--nr-accent-bg);
  font-size: 12px;
  font-weight: 700;
}
.add-link {
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  color: var(--nr-on-accent);
  border-radius: 10px;
  background: var(--nr-accent);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: background 180ms ease;
  &:hover { background: var(--nr-accent-hover); }
}
.app-main { min-height: 100vh; margin-left: 216px; padding: 0 40px; }
.app-stage { width: min(100%, 1280px); min-height: 100vh; margin: 0 auto; }
.mobile-topbar { display: none; }
.bottom-nav { display: none; }

@media (min-width: 768px) and (max-width: 1199px) {
  .side-nav { width: 80px; align-items: center; padding-inline: 8px; }
  .brand { padding: 0; }
  .brand-name, .nav-link span:not(.avatar), .add-link span { display: none; }
  .nav-links, .nav-bottom { width: 100%; }
  .nav-link { justify-content: center; padding: 0; }
  .add-link { width: 48px; min-height: 48px; align-self: center; }
  .app-main { margin-left: 80px; padding-inline: 24px; }
}

@media (max-width: 767px) {
  .side-nav { display: none; }
  .app-main { min-height: 100vh; margin-left: 0; padding: 0 16px calc(94px + env(safe-area-inset-bottom)); }
  .app-stage { min-height: calc(100vh - 94px); }
  .mobile-topbar { display: flex; min-height: 58px; align-items: center; justify-content: space-between; margin-inline: -16px; padding: 7px 16px; border-bottom: 1px solid var(--nr-border); background: var(--nr-surface); }
  .mobile-brand { display: inline-flex; align-items: center; gap: 8px; color: var(--nr-text); font-family: var(--nr-font-display); font-size: 17px; letter-spacing: .05em; text-decoration: none; }
  .mobile-brand .brand-mark { width: 32px; height: 32px; flex-basis: 32px; border-radius: 9px; }
  .mobile-actions { display: flex; align-items: center; gap: 7px; }
  .mobile-icon, .mobile-avatar { display: grid; width: 40px; height: 40px; place-items: center; color: var(--nr-text-2); border: 0; border-radius: 50%; background: transparent; cursor: pointer; text-decoration: none; }
  .mobile-icon:hover { background: var(--nr-surface-2); }
  .mobile-avatar { color: var(--nr-accent-text); background: var(--nr-accent-bg); font-size: 14px; font-weight: 700; }
  .bottom-nav {
    position: fixed;
    z-index: 100;
    right: 0;
    bottom: 0;
    left: 0;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    min-height: calc(62px + env(safe-area-inset-bottom));
    padding: 5px 8px env(safe-area-inset-bottom);
    border-top: 1px solid var(--nr-border);
    background: color-mix(in srgb, var(--nr-surface) 94%, transparent);
    backdrop-filter: blur(18px);
  }
  .mobile-link {
    display: flex;
    min-width: 44px;
    min-height: 48px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    color: var(--nr-text-3);
    border-radius: 10px;
    font-size: 10px;
    text-decoration: none;
    &.is-current { color: var(--nr-accent); font-weight: 700; }
  }
}
</style>
